import type { RuleSet } from '../../../rules';
import { computeVision, type EntityId, isCity, type PlayerId } from '../../../sim';
import { cityName } from '../../cities';
import type { Frame } from '../../frame';
import { labelOrDefault } from '../../names';
import { tickEngagements } from '../../tickCache';
import { engagedOwn, type StateSummary, summarize } from '../summary';
import type { GameEvent } from '../types';
import { unitArrived } from './arrivals';
import { buildingAttacked, scoutsLost, unitAttacked, withAttackers } from './attacks';
import { capAlerts } from './cap';
import type { DiffContext } from './context';
import { discoveryEvents } from './discovery';
import { createFoodTracker, type FoodTracker, foodAlert } from './food';
import { idleEvents, idleWorkers } from './idle';
import { buildingsLost, unitsLost } from './losses';
import { plunderEvents } from './plunder';
import { ownSpyExposed, rivalSpiesExposed, spiesCaught } from './spies';
import { trainedEvents } from './trained';
import { levelEvents, upgradePausedAlerts } from './upgrades';

/**
 * Notifications are derived on the client from successive states (the same
 * for the host and for peers adopting snapshots), one deriver per kind of
 * event: finding a rival (discovery), attacks on the player's things and
 * scouts or spies lost (attacks), units arriving where they were sent
 * (arrivals), spies exposed or caught (spies), units and buildings lost
 * (losses), plunder and razing (plunder), units done training (trained), and
 * automated workers with no work (idle). Finding a rival and attacks carry a `target` troops can be sent at
 * (see sendTroops.ts).
 */

/**
 * The events between two summaries, in the order the feed takes them:
 * attacks on own buildings, then per own unit its attack, arrival and
 * exposure, rival spies exposed or caught, scouts and spies lost, units and
 * buildings lost, plunder and razing, then units done training.
 */
export function diffEvents(
  prev: StateSummary,
  next: StateSummary,
  playerId: PlayerId,
  rules: RuleSet,
  /** Names a lost city ("City 2"); without it, the building type's name. */
  cityLabel?: (cityId: EntityId) => string,
  /** The player's units/buildings a rival is hitting this tick (`engagedOwn`). */
  engaged?: ReadonlySet<EntityId>,
  /** Sites the player cancelled: gone, but not lost. */
  cancelled?: ReadonlySet<EntityId>,
  /** What to call a player; the id without it. */
  playerName: (id: PlayerId) => string = (id) => id,
  /** The player's own group names (client-local), for "Wolves wiped out". */
  groupName?: (groupId: EntityId) => string | undefined
): GameEvent[] {
  const c: DiffContext = {
    groupName,
    prev,
    next,
    playerId,
    rules,
    cityLabel,
    engaged,
    cancelled,
    unitName: (type) => rules.units[type]?.name ?? type,
    playerName,
    buildingName: (type) => rules.buildings[type]?.name ?? type,
    cities: [...next.buildings].filter(([, b]) => b.owner === playerId && isCity(b, rules)),
  };
  const events: GameEvent[] = [];
  const push = (e: GameEvent | null) => {
    if (e) events.push(e);
  };
  for (const [id, b] of next.buildings) push(buildingAttacked(c, id, b));
  for (const [id, u] of next.units) {
    if (u.owner !== playerId) continue;
    const before = prev.units.get(id);
    if (!before) continue;
    push(unitAttacked(c, id, u, before));
    push(unitArrived(c, u, before));
    push(ownSpyExposed(c, u, before));
  }
  events.push(
    ...rivalSpiesExposed(c),
    ...spiesCaught(c),
    ...scoutsLost(c),
    ...unitsLost(c),
    ...buildingsLost(c),
    ...plunderEvents(c),
    ...trainedEvents(c)
  );
  return events;
}

/** What `frameEvents` remembers from one frame to the next. */
export interface NotificationTracker {
  /** The last frame's summary (null before the first). */
  prev: StateSummary | null;
  /** Rivals already found. */
  met: Set<PlayerId>;
  /** Idle workers already reported per city (see idleEvents). */
  idle: Map<EntityId, number>;
  /** The viewer's recent food stock, for the net rate (see food.ts). */
  food: FoodTracker;
}

export function createNotificationTracker(): NotificationTracker {
  return { prev: null, met: new Set(), idle: new Map(), food: createFoodTracker() };
}

/**
 * This frame's new events for the feed (none on the first frame, which only
 * records where things stand). Shown things come from the frame's view, the
 * fog-safe one: vision from the real state (a rival spy shown as an own
 * worker lends no sight), and only to say what's in sight.
 */
export function frameEvents(
  tracker: NotificationTracker,
  frame: Frame,
  /** Sites the player cancelled (their removal isn't a loss). */
  cancelled?: ReadonlySet<EntityId>
): GameEvent[] {
  const { view: state, real, playerId, rules, ctx, cityNumbers, playerName, names } = frame;
  const prev = tracker.prev;
  // Conditions are emitted every frame while they hold (see `addEvents`).
  const food = foodAlert(tracker.food, state, rules, playerId, cityNumbers, names);
  const caps = capAlerts(state, rules, playerId, cityNumbers, names);
  const paused = upgradePausedAlerts(state, rules, playerId, cityNumbers, names);
  // With prev: a barracks outliving its city keeps that city's name.
  const next = summarize(state, rules, playerId, prev ?? undefined, real);
  tracker.prev = next;
  if (!prev) return [];
  // Fallen cities keep their numbers (numberCities never forgets one).
  const lostCityLabel = (id: string) => {
    const n = cityNumbers.get(id);
    return labelOrDefault(names, id, n === undefined ? 'City' : `City ${n}`);
  };
  // Computed at most once and only when an event needs it.
  let visible: ReadonlySet<string> | undefined;
  const vision = () => {
    visible ??= computeVision(real, rules, playerId);
    return visible;
  };
  return [
    ...(food ? [food] : []),
    ...caps,
    ...paused,
    ...levelEvents(prev, next, state, ctx, playerId, cityNumbers, names),
    ...discoveryEvents(state, rules, playerId, tracker.met, vision, playerName),
    ...withAttackers(
      diffEvents(
        prev,
        next,
        playerId,
        rules,
        lostCityLabel,
        engagedOwn(state, ctx, playerId, tickEngagements(state, ctx)),
        cancelled,
        playerName,
        (groupId) => names[groupId]
      ),
      state,
      ctx,
      playerId,
      vision,
      () => tickEngagements(state, ctx),
      playerName
    ),
    ...idleEvents(state, idleWorkers(state, ctx, playerId), tracker.idle, (id) => {
      const city = state.buildings[id];
      return city ? cityName(city, playerId, cityNumbers, playerName, names) : 'a city';
    }),
  ];
}
